import { EventManager, Patch } from "@ploy-lib/calculation";
import { isNotNull } from "@ploy-lib/core";
import { MinimalInitialData } from "@ploy-lib/rest-resources";
import { parseQuery } from "@ploy-lib/routing";

interface ExternalQueryStringParamsSession {
	agent: string;
	vendorId?: string;
	token?: string;
}

interface ExternalQueryStringParamsInstructions extends MinimalInitialData {
	marketId?: string;

	objectPrice?: string;
	equity?: string;
	termsInYears?: string;
	totalTermsInYears?: string;
	totalTermsCountInYears?: string;
	termsCount?: string;
	campaignId?: string;

	objectTypeId?: number;
	objectSubTypeId?: number;
	objectCondition?: number;
	make?: string;
	model?: string;
	yearModel?: string;
	manualDefined?: boolean;
	mileage?: string;
	regNumber?: string;
	objectImageUrl?: string;

	customerTypeId?: number;
}

const alternativeMapping: Record<
	string,
	keyof ExternalQueryStringParamsInstructions
> = {
	finnkode: "marketId",
	pris: "objectPrice",
	egenkapital: "equity",
	nedbetalingstid: "termsInYears",
	// nybil is supposedly boolean, will this work?
	nybil: "objectCondition",
	arsmodell: "yearModel",
	merke: "make",
	modell: "model",
	kilometerstand: "mileage",
	registreringsnummer: "regNumber",
	bilde: "objectImageUrl",
	kampanjeid: "campaignId"
} as const;

type ExternalQueryStringParams = ExternalQueryStringParamsSession &
	ExternalQueryStringParamsInstructions;

type DefinedPatch = Patch<string, string | number | boolean>;
type Param = Partial<Omit<DefinedPatch, "value">> &
	Pick<DefinedPatch, "target" | "namespace"> & {
		source: keyof ExternalQueryStringParamsInstructions;
	};

const paramMapping: Param[] = [
	{ source: "objectTypeId", namespace: "Object", target: "Category" },
	{ source: "objectSubTypeId", namespace: "Object", target: "CarType" },
	{
		source: "marketId",
		namespace: "Object",
		target: "ExternalReference"
	},
	{
		source: "marketId",
		namespace: "Object",
		target: "MarketplaceSearch",
		clickTrigger: true
	},
	{
		source: "marketId",
		namespace: "Object",
		target: "MarketplaceAndRegNrSearch",
		clickTrigger: true
	},

	// Equity _must_ be set first due some weird issue in calculator and field functions
	{ source: "equity", namespace: "Calculator", target: "Equity" },
	{ source: "objectPrice", namespace: "Calculator", target: "ObjectPrice" },
	// Bit unsure about the foloowing three, Calculator.TermsInYears sets the _first_ payeries
	{
		source: "termsInYears",
		namespace: "Calculator",
		target: "TermsInYears"
	},
	// These two set the _total_ terms, these would probably be the best match wrt. campains
	{
		source: "totalTermsCountInYears",
		namespace: "Calculator",
		target: "TotalTermsCountInYears"
	},
	{
		source: "totalTermsInYears",
		namespace: "Calculator",
		target: "TotalTermsInYears"
	},
	{ source: "termsCount", namespace: "Calculator", target: "TermsCount_0" },
	{ source: "objectTypeId", namespace: "Calculator", target: "Category" },
	{ source: "objectSubTypeId", namespace: "Calculator", target: "CarType" },
	{ source: "campaignId", namespace: "Calculator", target: "Campaign" },
	{ source: "make", namespace: "Object", target: "Make" },
	{ source: "model", namespace: "Object", target: "Model" },
	{ source: "yearModel", namespace: "Object", target: "YearModel" },
	{ source: "manualDefined", namespace: "Object", target: "IsManualDefined" },
	{ source: "mileage", namespace: "Object", target: "Mileage" },
	{ source: "regNumber", namespace: "Object", target: "RegNumber" },
	{ source: "objectCondition", namespace: "Object", target: "ObjectCondition" },
	{ source: "objectImageUrl", namespace: "Object", target: "ImageUrl" },
	{ source: "customerTypeId", namespace: "Customer", target: "CustomerType" }
];

export function parseQueryStringParams(
	queryString: string
): ExternalQueryStringParams | null {
	const qsParams = parseQuery({ search: queryString });

	if (!qsParams || !qsParams.ext || !(qsParams.ext.agent || qsParams.ext.token))
		return null;

	const params: ExternalQueryStringParams = qsParams.ext;

	const { ext, ...otherParams } = qsParams;

	for (let [alternativeName, extName] of Object.entries(alternativeMapping)) {
		// Only set alternative value if the target is unset
		if (params[extName] == null && otherParams[alternativeName] != null) {
			params[extName] = otherParams[alternativeName];
		}
	}

	return params;
}

export const bootstrapQueryStringForAgentSetup = (
	eventProvider: EventManager<string, string | number | boolean>
) => {
	const unsubscribe = eventProvider.calculatorReady.subscribe(() => {
		unsubscribe();

		const commonProps: Partial<DefinedPatch> = {
			overwrite: true,
			isResolve: true,
			noDefer: true
		};

		const queryString = window.location.search;
		const params = parseQueryStringParams(queryString);

		if (!params) return;

		if (params["manualDefined"] === undefined) params["manualDefined"] = true;

		const patches: DefinedPatch[] = paramMapping
			.map(({ source, ...patch }) => {
				if (params[source] == null) return null;

				return {
					...commonProps,
					...patch,
					value: params[source]
				};
			})
			.filter(isNotNull);

		if (patches.length > 0) {
			patches.push({
				...commonProps,
				namespace: "Calculator",
				target: "SoftVal",
				value: 0
			});
			eventProvider.patch.notify({ patches });
		}
	});
};
